import { useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import { object, string, type ObjectSchema } from "yup";
import { Button, Flex, ModalV2 } from "@jsq/ds/core";
import { Form, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import {
  NAME_NOT_UNIQUE_ERROR,
  useDataPackageNameValidator,
  type DataPackageNameValidator,
} from "../useDataPackageNameValidator";

export type EditDataPackageSettingsFormValues = {
  name: string;
  description: string;
};

const MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 200;

const createValidationSchema = (
  validateName: DataPackageNameValidator,
): ObjectSchema<EditDataPackageSettingsFormValues> =>
  object({
    name: string()
      .required("Name this data package")
      .trim()
      .min(MIN_LENGTH, `Must be at least ${MIN_LENGTH} characters`)
      .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`)
      .test("unique-name", NAME_NOT_UNIQUE_ERROR, async function testUnique(value) {
        if (!value) return true;
        const error = await validateName(value);
        if (!error) return true;
        return this.createError({ message: error });
      }),
    description: string()
      .trim()
      .test(
        "min-if-provided",
        `Must be at least ${MIN_LENGTH} characters`,
        (value) => !value || value.length >= MIN_LENGTH,
      )
      .max(DESCRIPTION_MAX_LENGTH, `Maximum ${DESCRIPTION_MAX_LENGTH} characters`)
      .default("")
      .defined(),
  });

const SaveSettingsButton = ({ isSaving }: { isSaving: boolean }) => {
  const { values, errors } = useFormikContext<EditDataPackageSettingsFormValues>();
  const isNameTooShort = values.name.trim().length < MIN_LENGTH;
  const hasErrors = Object.keys(errors).length > 0;
  return (
    <SubmitButton
      isLoading={isSaving}
      disabled={isNameTooShort || hasErrors}
      data-testid="save-data-package-settings-button"
    >
      Update settings
    </SubmitButton>
  );
};

export type EditDataPackageSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: EditDataPackageSettingsFormValues) => Promise<void>;
  isSaving: boolean;
  initialValues: EditDataPackageSettingsFormValues;
  arenaId: number | null;
  dataPackageId: number;
};

export const EditDataPackageSettingsModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialValues,
  arenaId,
  dataPackageId,
}: EditDataPackageSettingsModalProps) => {
  const handleSubmit = useCallback(
    async (values: EditDataPackageSettingsFormValues) => {
      await onSave(values);
    },
    [onSave],
  );

  const validateName = useDataPackageNameValidator({ arenaId, dataPackageId });
  const validationSchema = useMemo(() => createValidationSchema(validateName), [validateName]);

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title="Data package settings"
      data-testid="edit-data-package-settings-modal"
    >
      <Form<EditDataPackageSettingsFormValues>
        initialValues={initialValues}
        yupValidationSchema={validationSchema}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <FormTextInput
              name="name"
              label="Data package name"
              placeholder="Enter data package name"
              data-testid="edit-data-package-name"
              isFullWidth
            />

            <FormTextInput
              name="description"
              label="Description"
              placeholder="Enter description"
              data-testid="edit-data-package-description"
              isFullWidth
            />
          </Flex>
        </ModalV2.Body>

        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <Button variant="neutral" onClick={onClose}>
              Cancel
            </Button>
            <SaveSettingsButton isSaving={isSaving} />
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
